import React, { useState, useEffect } from 'react';
import { trustService, searchClientsAsync } from '../../services/api';
import { X, Shield, AlertCircle, Check } from 'lucide-react';
import useModal from '../../hooks/useModal';
import AsyncSearchableSelect from '../common/AsyncSearchableSelect';

const NewTrustAccountModal = ({ isOpen, onClose, onAccountCreated }) => {
  const [formData, setFormData] = useState({
    client_id: '',
    account_number: '',
    initial_deposit: '',
    earns_interest: true,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(isOpen, onClose);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        client_id: '',
        account_number: '',
        initial_deposit: '',
        earns_interest: true,
        notes: ''
      });
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.client_id) {
        setError('Please select a client');
        return;
      }

      // Validate initial deposit if provided
      if (formData.initial_deposit) {
        const deposit = parseFloat(formData.initial_deposit);
        if (isNaN(deposit) || deposit < 0) {
          setError('Please enter a valid initial deposit amount');
          return;
        }
      }

      const accountData = {
        client_id: formData.client_id,
        account_number: formData.account_number || null,
        initial_deposit: formData.initial_deposit ? parseFloat(formData.initial_deposit) : null,
        earns_interest: formData.earns_interest,
        notes: formData.notes || null
      };

      const newAccount = await trustService.createTrustAccount(accountData);
      
      // Call the callback to refresh the parent component
      if (onAccountCreated) {
        onAccountCreated(newAccount);
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating trust account:', error);
      setError(error.response?.data?.detail || 'Failed to create trust account');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Shield className="h-6 w-6 text-yellow-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Create New Trust Account</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {/* Client Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client *
            </label>
            <AsyncSearchableSelect
              value={formData.client_id}
              onChange={(val) => setFormData(prev => ({ ...prev, client_id: val }))}
              onSearch={searchClientsAsync}
              emptyOption="Select a client"
              required
            />
          </div>

          {/* Account Number (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Number (Optional)
            </label>
            <input
              type="text"
              name="account_number"
              value={formData.account_number}
              onChange={handleInputChange}
              maxLength={50}
              placeholder="Leave blank for auto-generation"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              If left blank, an account number will be automatically generated based on your firm.
            </p>
          </div>

          {/* Initial Deposit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Initial Deposit (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">$</span>
              </div>
              <input
                type="number"
                name="initial_deposit"
                value={formData.initial_deposit}
                onChange={handleInputChange}
                onWheel={(e) => e.target.blur()}
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Optional initial deposit to fund the trust account upon creation.
            </p>
          </div>

          {/* IOLTA Compliance */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              IOLTA Compliance
            </label>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="earns_interest"
                  checked={formData.earns_interest}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">
                  This account earns interest (IOLTA compliant)
                </span>
              </label>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">IOLTA Compliance Note</p>
                    <p>
                      Most trust accounts should earn interest per IOLTA (Interest on Lawyers' Trust Accounts) 
                      regulations. Interest earned goes to legal aid programs. Only accounts with nominal amounts 
                      or short durations may be non-interest bearing.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              maxLength={1000}
              placeholder="Any additional notes about this trust account setup..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>

          {/* Account Preview */}
          {formData.client_id && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Account Preview</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>Account Type: {formData.earns_interest ? 'Interest-bearing (IOLTA)' : 'Non-interest bearing'}</div>
                {formData.initial_deposit && (
                  <div className="font-medium text-green-600">
                    Initial Deposit: {trustService.formatCurrency(formData.initial_deposit)}
                  </div>
                )}
                {formData.account_number && <div>Custom Account Number: {formData.account_number}</div>}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-yellow-500 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.client_id}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 border border-transparent rounded-lg hover:bg-yellow-600 focus:ring-2 focus:ring-yellow-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Create Trust Account
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewTrustAccountModal;